/**
 * Turn a company's partnerships into the options of the "add partner" picker.
 *
 * Three things used to make a real partner vanish from this list, so each is a
 * rule here and each is tested:
 *  - it only held the first page of partnerships (see `useAllPartnerships`);
 *  - it showed `partnerCompanyId`, which is the VIEWER's own company on any
 *    partnership the viewer did not create, and dropped the real counterparty;
 *  - it filtered to ACTIVE, but a partnership created in the app starts PENDING
 *    until the other side accepts — so a partner added a minute ago was silently
 *    missing.
 *
 * Partnerships that are not active yet are still listed, greyed out with the
 * reason. "It is here but cannot be used yet" is a different answer from "it is
 * not here", and only the first stops people hunting for a bug.
 */
import type { EnrichedPartnership } from '../../api/partnerships';

export interface PartnerOption {
  value: string;
  label: string;
  description?: string;
  disabled: boolean;
}

const STATUS_REASON: Record<string, string> = {
  PENDING: 'Awaiting their acceptance — available once they accept',
  SUSPENDED: 'Partnership suspended',
  INACTIVE: 'Partnership inactive',
  REJECTED: 'Partnership was declined',
};

export function buildPartnerOptions(partnerships: readonly EnrichedPartnership[]): PartnerOption[] {
  const byCompany = new Map<string, PartnerOption>();

  for (const p of partnerships) {
    const companyId = p.counterpartyCompanyId ?? p.partnerCompanyId;
    if (!companyId) continue;
    const name = p.counterpartyCompany?.name ?? p.partnerCompany?.name;
    const active = p.status === 'ACTIVE';
    const types = p.partnerTypes?.length ? `Types: ${p.partnerTypes.join(', ')}` : undefined;
    const option: PartnerOption = {
      value: companyId,
      label: name || `Company ${companyId}`,
      description: active
        ? types
        : [STATUS_REASON[p.status] ?? `Status: ${p.status}`, types].filter(Boolean).join(' · '),
      disabled: !active,
    };

    // A company can sit on more than one partnership (one per partner type, say).
    // One option per company is what the person is choosing between — and if ANY
    // of its partnerships is active, the company is choosable.
    const existing = byCompany.get(companyId);
    if (!existing || (existing.disabled && !option.disabled)) byCompany.set(companyId, option);
  }

  // Choosable first, so greyed-out entries never push a usable partner off the
  // top of a long list.
  return [...byCompany.values()].sort(
    (a, b) => Number(a.disabled) - Number(b.disabled) || a.label.localeCompare(b.label)
  );
}

/**
 * How a partner's role and access level read on screen (N14): "Customs agent"
 * and "Full access", not the stored codes. A role the list does not know reads
 * in plain words ("PORT_AGENT" → "Port agent"); what is stored is unchanged.
 */
const ROLE_LABELS: Record<string, string> = {
  CUSTOMS_AGENT: 'Customs agent',
  FORWARDER: 'Freight forwarder',
  CARRIER: 'Carrier',
  SHIPPER: 'Shipper',
  CONSIGNEE: 'Consignee',
  BROKER: 'Broker',
  BUYER: 'Buyer',
  SELLER: 'Seller',
  SUPPLIER: 'Supplier',
  END_CLIENT: 'End client',
  ISSUER: 'Issuer',
  RECIPIENT: 'Recipient',
  PAYER: 'Payer',
  PAYMENT_PROCESSOR: 'Payment processor',
  OPERATOR: 'Operator',
  CLIENT: 'Client',
  LOGISTICS_PROVIDER: 'Logistics provider',
  OWNER: 'Owner',
  CUSTODIAN: 'Custodian',
};

const ACCESS_LEVEL_LABELS: Record<string, string> = {
  READ_ONLY: 'Read only',
  READ_WRITE: 'Read and write',
  FULL: 'Full access',
};

const plainWords = (code: string): string => {
  const words = code.trim().toLowerCase().replace(/[_-]+/g, ' ');
  return words === '' ? code : `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};

export const partnerRoleLabel = (role: string): string => ROLE_LABELS[role] ?? plainWords(role);

export const accessLevelLabel = (level: string): string =>
  ACCESS_LEVEL_LABELS[level] ?? plainWords(level);
